import { useState, useEffect, useRef, useMemo } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { toast } from 'react-toastify'
import Server from "../../Shared/Server"
import Spinner from '../../Shared/Spinner'
import { categories_with_id } from "../../Shared/List";
import { InputField } from "../../Shared/InputField"
import SelectField from "../../Shared/SelectField"
import { FaFileExport } from "react-icons/fa"
import {AgGridReact} from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

const DailyCollectionSummary = () => {
    const [errors, setErrors] = useState({});
    const [collection, setCollection] = useState([]);
    const [pinnedBottomRowData, setPinnedBottomRowData] = useState([]);
    const [byOptions, setByOptions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [title, setTitle] = useState('');
    const gridRef = useRef();
    const navigate = useNavigate();
    const location = useLocation();

    const [formData, setFormData] = useState({
        fdate: new Date().toISOString().slice(0, 10),
        tdate: new Date().toISOString().slice(0, 10),
        by: 1,
    })
    const {fdate, tdate, by} = formData

    const fetchData = async (payload) => {
        setTitle(`Colletion Daily Report as ${fdate} to ${tdate} by ${categories_with_id[formData.by - 1].name}`);
        setIsLoading(true);

        try {
            const response = await Server.post('/reports/daily-collection', payload)
            const categories = []
            categories_with_id.map(category => (
                categories.push({
                    id: category.id,
                    key: category.key,
                    name: category.name
                })
            ))

            setCollection(response?.data);
            setByOptions(categories);
            setIsLoading(false);

            const output = response?.data.reduce((data, row) => {
                data.customers += row.customers
                data.due += parseFloat(row.due);
                data.paid += parseFloat(row.paid);
                data.discount += parseFloat(row.discount);
                data.balance += parseFloat(row.balance);
                return data;
            }, {customers: 0, due: 0.00, paid: 0.00, paid: 0.00, discount: 0.00, balance: 0.00});

            setPinnedBottomRowData([
                {
                    customers: output.customers,
                    due: parseFloat(output.due).toFixed(2),
                    paid: parseFloat(output.paid).toFixed(2),
                    discount: parseFloat(output.discount).toFixed(2),
                    balance: parseFloat(output.balance).toFixed(2),
                }
            ]);
        } catch (error) {
            setIsLoading(false)
            if(error?.response?.status === 401){
                localStorage.removeItem('user')
                navigate('/login')
            }else if (error?.response?.status === 422) {
                setErrors({})
                error.response?.data?.detail.map((err) => (
                    setErrors((prevState) => ({
                        ...prevState,
                        [err.loc[1]]: err.msg
                    }))
                ))
            }else if (error?.response?.status === 403){
                toast.error(error?.response?.data?.detail || 'Forbidden');
                navigate('/');
            }else {
                toast.error(error?.response?.data?.detail || 'Failed to load collection summary')
            }
        }
    }

    useEffect(() => {
  if (location.state?.fdate && location.state?.tdate && location.state?.by) {
    const matchedCategory = categories_with_id.find(c => c.name.toLowerCase() === location.state.by.toLowerCase());
    const by = matchedCategory?.name || categories_with_id[0].name;
    setFormData({
      fdate: location.state.fdate,
      tdate: location.state.tdate,
      by: matchedCategory?.id || 1,
    })

    fetchData({
      fdate: location.state.fdate,
      tdate: location.state.tdate,
      by,
    });
    setTitle(`Colletion Daily Report as ${location.state.fdate} to ${location.state.tdate} by ${by}`);
  } else {
    // Default load
    const by = categories_with_id[formData.by - 1].name
    fetchData({ fdate, tdate, by })
  }
}, [])

    const onChange = (e) => {
        setFormData((prevState) => ({
            ...prevState,
            [e.target.name]: e.target.value
        }))

    }

    const onDoubleClick = (payload) => {
        navigate(`/daily-collection-detail`, { state: {data: payload}})
    }

    const onSubmit = (e) => {
        e.preventDefault()

        //find category name with its index
        const by = categories_with_id[formData.by - 1].name
        const payload = {fdate, tdate, by}
        fetchData(payload)

    }

    const columnDefs = useMemo(() => [
        {
            headerName: "#",
            filter: false,
            maxWidth: 70,
            sortable: true,
            valueFormatter: function (params) {
            if (isNaN(params.node.id)) {
                return "";
            }
            return `${Number(params.node.rowIndex) + 1}`;
            },
        },

        {
            headerName: "Name",
            field: "name",
            sortable: true,
            filter: true,
        },
        {
            headerName: "Customer",
            field: "customers",
            sortable: true,
            filter: 'agNumberColumnFilter',
        },
        {
            headerName: "Due",
            field: "due",
            sortable: true,
            filter: 'agNumberColumnFilter',
        },
        {
            headerName: "Paid",
            field: "paid",
            sortable: true,
            filter: 'agNumberColumnFilter',
        },
        {
            headerName: "Discount",
            field: "discount",
            sortable: true,
            filter: 'agNumberColumnFilter',
        },
        {
            headerName: "Balance",
            field: "balance",
            sortable: true,
            filter: 'agNumberColumnFilter',
        },
    ], []);

    const defaultColDef = useMemo(() => {
        return {
            filterParams: {
                buttons: []
            }
        };
    }, []);

    const onFirstDataRendered = (params) => {
        params.api.autoSizeAllColumns();
    }

    const onGridSizeChanged = (params) => {
        params.api.autoSizeAllColumns();
    }

    let gridApi

    const onGridReady=(params)=>{
        gridApi=params.api
    }
      const onExportClick=()=>{
        gridApi.exportDataAsCsv({ fileName: `${title}.csv` })
    }

    const getRowStyle = (params) => {
        if (params.node.rowPinned) {
          return {
            backgroundColor: '#f8f9fa',
            fontWeight: 'bold',
            color: '#000',
          };
        }
        return {};
    };

    if(isLoading){
        return <Spinner/>
    }

  return (
    <>
        <form onSubmit={onSubmit}>
            <div className="row">
                <div className="col-md-2">
                    <InputField
                        name="fdate"
                        type="date"
                        required="required"
                        value={fdate}
                        onChange={onChange}
                        error={errors.fdate}
                    />
                </div>
                <div className="col-md-2">
                    <InputField
                        name="tdate"
                        type="date"
                        required="required"
                        value={tdate}
                        onChange={onChange}
                        error={errors.tdate}
                        max={new Date(new Date().setMonth(new Date().getMonth() + 3)).toISOString().split('T')[0]}
                    />
                </div>
                <div className="col-md-2">
                    <SelectField
                        name="by"
                        value={parseInt(by)}
                        options={byOptions}
                        onChange={onChange}
                        error={errors.by}
                    />
                </div>
                <div className="col-md-4">
                    <input type="submit" className="btn btn-info mt-1" value="Apply Filter" style={{float: 'left'}}/>
                </div>
                <div className="col-md-2">
                    <button onClick={()=>onExportClick()} className="btn btn-success mb-2" style={{float: 'right'}}><FaFileExport/> &nbsp;Export</button>
                </div>
            </div>
            <br/>
        </form>
        <div className="card mt-0">
            <div className="card-header p-2 bg-info text-white d-flex justify-content-center">
                    <h3 className="card-title">{title}</h3>
            </div>
            <div className="card-body">
                <div className="row">
                    <div className="col-md-12">
                        <div className="ag-theme-alpine responsive-aggrid" style={{ height: 400, width: '100%' }}>
                        <AgGridReact
                            onRowDoubleClicked={(e) => {
                                if (e.node.rowPinned) return;
                                else if (e.data.customers < 1) return;
                                onDoubleClick({fdate, tdate, public_id: e.data.public_id, name: e.data.name, by: categories_with_id[formData.by - 1].name.toLowerCase()})
                            }}
                            onFirstDataRendered={onFirstDataRendered}
                            onGridSizeChanged={onGridSizeChanged}
                            defaultColDef={defaultColDef}
                            suppressMenuHide={true}
                            rowData={collection}
                            domLayout={"normal"}
                            rowSelection={"multiple"}

                            animateRows={true}

                            pagination={false}

                            resizable={true}
                            enableCellTextSelection={true}
                            suppressCellFocus={true}

                            ref={gridRef}
                            onGridReady={onGridReady}
                            getRowStyle={getRowStyle}
                            pinnedBottomRowData={pinnedBottomRowData}
                            columnDefs={columnDefs}
                        ></AgGridReact>{" "}
                    </div>
                    </div>
                </div>
            </div>
        </div>
      <br/>
    </>
  )
}

export default DailyCollectionSummary